import { useEffect, useState, useRef } from 'react';

export const useCountDown = (initialTime?: number) => {
  const [countDown, setCountDown] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset countdown when new initial time is provided
  useEffect(() => {
    if (initialTime && initialTime > 0) {
      setCountDown(initialTime);
      setIsActive(true);
    }
  }, [initialTime]);

  // Handle countdown logic
  useEffect(() => {
    if (!isActive || !countDown || countDown <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCountDown((prev) => {
        if (!prev || prev <= 1) {
          setIsActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [countDown, isActive]);

  const resetCountDown = (newTime?: number) => {
    const timeToSet = newTime ?? initialTime;
    if (timeToSet && timeToSet > 0) {
      setCountDown(timeToSet);
      setIsActive(true);
    }
  };

  const pauseCountDown = () => {
    setIsActive(false);
  };

  const resumeCountDown = () => {
    if (countDown && countDown > 0) {
      setIsActive(true);
    }
  };

  const stopCountDown = () => {
    setIsActive(false);
    setCountDown(0);
  };

  // Format countdown for display
  const formatTime = (seconds: number | null): string => {
    if (!seconds || seconds <= 0) return '00:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return {
    countDown,
    isActive,
    isExpired: countDown === 0,
    isLoading: countDown === null,
    formattedTime: formatTime(countDown),
    resetCountDown,
    pauseCountDown,
    resumeCountDown,
    stopCountDown,
  };
};
